import clsx from 'clsx';
import lodashGet from 'lodash/get';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import useCellRenderers from './useCellRenderers';
import { HistoryTriggerButton } from './components/CellRenderers/HistoryTriggerButton';
import historyStyles from './components/CellRenderers/HistoryTriggerButton.module.scss';
import { ColumnMetadata, DataTableColumn, DataTableColumnType, DataTableProps } from './DataTable.types';
import { COMPANY_NAME_FIELD, DISABLED_ROW_TOOLTIP_MESSAGE } from './DataTable.constants';
import DataTableFilterDrawer from './components/DataTableFilterDrawer/DataTableFilterDrawer';
import DataTableColumnCustomizationDrawer from './components/DataTableColumnCustomizationDrawer/DataTableColumnCustomizationDrawer';

import ShimmerLoading from './components/ShimmerLoading/ShimmerLoading';

import styles from './DataTable.module.scss';
import { useAppSelector } from '../../../app/hooks';
import { COMMENTS_THREAD_SOURCES } from '../../../store/commentsThread/commentsThread_reducer';
const { DEAL, INVESTOR_DEAL, COMPANIES_LIST } = COMMENTS_THREAD_SOURCES;
import { useDispatch, useSelector } from 'react-redux';
import { setCustomFilterDrawer } from '../../../store/investor/action';
import { isEmpty } from 'lodash';
import { openColumnCustomizationDrawer, setColumnCustomizationState } from '../../../store/investor/action';
import { selectColumnCustomization, selectSelectedColumns } from '../../../store/investor/selectors';
import { configToIds, idsToConfig } from './utils/columnTransformUtils';
import { useFeatureVisibility } from '../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../utils/constants/visibilityKeys';
import { isLenderType } from '../../../utils/utils';
import { setTooltipContent, removeTooltipContent } from '../../../store/tooltip/tooltip_actions';

const DataTable: React.FC<DataTableProps> = ({
    columns,
    rows,
    totalRowCount,
    emptyState,
    noSearchResults,
    loading,
    onRowClick,
    customCellRenderers,
    onHistoryClick,
    pinnedColumns,
    openActionRowIndex,
    valueGetterMap,
    // Filter props
    onFiltersApply,
    filterSourceKey,
    // Column customization props
    enableColumnCustomization = false,
    masterColumnConfig,
    onColumnsChange,
    // Horizontal display props
    horizontal = false,
    horizontalStyle,
    horizontalRowStyle,
    horizontalLabelStyle,
    horizontalValueStyle,
    horizontalLabelSuffix = ':',
    disableShowFilter = false,
}) => {
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

    const location = useLocation();
    const tableRef = useRef<HTMLDivElement>(null);

    const customFilterDrawer = useSelector((state: any) => state.investor.customFilterDrawer);
    const columnCustomization = useSelector(selectColumnCustomization);
    const selectedColumns = useSelector(selectSelectedColumns);
    const dispatch = useDispatch();

    const cellRenderers = useCellRenderers(customCellRenderers);
    const userData = useAppSelector((state) => state.user.userData);
    const dealStatusConfig = useAppSelector((state) => state.investor.deals.dealStatusConfig);

    // Utility function to filter columns based on show property
    // Excludes columns with show: false or show: null, includes columns with show: undefined or show: true
    // If disableShowFilter is true, returns all columns regardless of show property
    const filterVisibleColumns = useCallback(
        (cols: DataTableColumn[]): DataTableColumn[] => {
            if (disableShowFilter) {
                return cols || [];
            }
            return (cols || []).filter((col) => col.show === undefined || col.show === true);
        },
        [disableShowFilter],
    );

    // Column customization logic - API config is the master, table renders derived config
    const masterConfig = masterColumnConfig; // API config is the master
    const [derivedColumns, setDerivedColumns] = useState<DataTableColumn[]>(filterVisibleColumns(columns));

    // Derive table columns from master config + saved selections
    useEffect(() => {
        if (!masterConfig || !enableColumnCustomization || !filterSourceKey) {
            // No customization: use provided columns directly (filter by show property)
            setDerivedColumns(filterVisibleColumns(columns));
            return;
        }

        // Use saved column selection if available for this source
        if (selectedColumns.length > 0 && columnCustomization.sourceKey === filterSourceKey) {
            const customizedColumns = idsToConfig(selectedColumns, masterConfig);
            setDerivedColumns(filterVisibleColumns(customizedColumns));
            return;
        }

        // Default: derive from master config (use all available columns, filter by show property)
        setDerivedColumns(filterVisibleColumns(masterConfig));
    }, [
        masterConfig,
        selectedColumns,
        columnCustomization.sourceKey,
        filterSourceKey,
        enableColumnCustomization,
        columns,
        filterVisibleColumns,
    ]);

    // Initialize Redux state from master config for this source (without opening drawer)
    useEffect(() => {
        if (!enableColumnCustomization || !filterSourceKey || !masterConfig) {
            return;
        }

        // Only initialize if we don't have state for this source yet
        if (columnCustomization.sourceKey === filterSourceKey) {
            return;
        }

        // Initialize with empty columns selection to match Companies List behavior
        // Users can then customize columns as needed
        dispatch(
            setColumnCustomizationState({
                open: false,
                sourceKey: filterSourceKey,
                selectedColumns: [],
                tempSelectedColumns: [],
                isModified: false,
            }),
        );
    }, [enableColumnCustomization, filterSourceKey, masterConfig, columnCustomization.sourceKey, dispatch]);

    // Handle opening column customization drawer
    const handleOpenColumnCustomization = useCallback(() => {
        if (enableColumnCustomization && filterSourceKey) {
            const currentColumnIds = configToIds(derivedColumns);
            dispatch(
                openColumnCustomizationDrawer({
                    sourceKey: filterSourceKey,
                    selectedColumns: currentColumnIds,
                }),
            );
        }
    }, [dispatch, enableColumnCustomization, filterSourceKey, derivedColumns]);

    // Handle applying column customization changes
    const handleApplyColumnCustomization = useCallback(
        (newColumns: DataTableColumn[]) => {
            const filteredColumns = filterVisibleColumns(newColumns);
            setDerivedColumns(filteredColumns);

            // Notify parent component if callback is provided
            if (onColumnsChange) {
                onColumnsChange(filteredColumns);
            }
        },
        [onColumnsChange, filterVisibleColumns],
    );

    const { dataPendingEnabledVisibility } = useFeatureVisibility({
        dataPendingEnabledVisibility: VISIBILITY_KEYS.DATA_PENDING.ENABLED,
    });

    const commentThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const commentThreadSource = commentThreadState.source;
    const commentThreadDealId = commentThreadState.dealId;

    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (location?.state?.scrollPosition && tableRef?.current)
            tableRef?.current?.scrollTo({
                top: location.state?.scrollPosition,
                behavior: 'smooth',
            });
    }, [location?.state?.scrollPosition]);

    // Memoize pinnedColumns lookup map for O(1) access
    const pinnedColumnsMap = useMemo(() => {
        const map = new Map<string, { field: string; position: 'left' | 'right' }>();
        pinnedColumns?.forEach((pinned) => {
            map.set(pinned.field, pinned);
        });
        return map;
    }, [pinnedColumns]);

    // Precompute all column metadata once
    const columnMetadata = useMemo(() => {
        const leftPinned: ColumnMetadata[] = [];
        const rightPinned: ColumnMetadata[] = [];
        const scrollable: ColumnMetadata[] = [];

        // First pass: separate columns and create metadata (use derived columns)
        (derivedColumns ?? []).forEach((column, index) => {
            const pinnedInfo = pinnedColumnsMap.get(column.field);
            const metadata: ColumnMetadata = {
                column: column,
                originalIndex: index,
                pinnedInfo,
                pinnedIndex: -1, // Will be set in second pass
                pinnedClass: '',
                style: {},
                headerStyle: {},
            };

            if (pinnedInfo?.position === 'left') {
                leftPinned.push(metadata);
            } else if (pinnedInfo?.position === 'right') {
                rightPinned.push(metadata);
            } else {
                scrollable.push(metadata);
            }
        });

        // Second pass: calculate pinned indices and styles
        const calculatePinnedStyles = (metadataArray: ColumnMetadata[], position: 'left' | 'right') => {
            let cumulativeOffset = 0;

            metadataArray.forEach((metadata, index) => {
                metadata.pinnedIndex = index;
                metadata.pinnedClass = position === 'left' ? styles.PinnedLeft : styles.PinnedRight;

                if (position === 'left') {
                    metadata.style = { left: `${Math.max(cumulativeOffset - index * 2, 0)}px` };
                    metadata.headerStyle = { left: `${Math.max(cumulativeOffset - index * 2, 0)}px` };
                } else {
                    // For right pinned, calculate from the right
                    const remainingCols = metadataArray.length - index - 1;
                    let rightOffset = 0;
                    for (let i = index + 1; i < metadataArray.length; i++) {
                        const col = metadataArray[i];
                        if (col.column.width && typeof col.column.width === 'string' && col.column.width.endsWith('%')) {
                            rightOffset += (parseFloat(col.column.width) * viewportWidth) / 100;
                        } else {
                            rightOffset += 150;
                        }
                    }
                    metadata.style = { right: `${Math.max(rightOffset - remainingCols * 2, 0)}px` };
                    metadata.headerStyle = { right: `${Math.max(rightOffset - remainingCols * 2, 0)}px` };
                }

                // Calculate cumulative offset for next column
                if (metadata.column.width && typeof metadata.column.width === 'string' && metadata.column.width.endsWith('%')) {
                    cumulativeOffset += (parseFloat(metadata.column.width) * viewportWidth) / 100;
                } else {
                    cumulativeOffset += 150;
                }
            });
        };

        calculatePinnedStyles(leftPinned, 'left');
        calculatePinnedStyles(rightPinned, 'right');

        // Order columns: left pinned, scrollable, right pinned
        const allOrdered = [...leftPinned, ...scrollable, ...rightPinned];

        // Add special styling for first/last pinned columns
        if (leftPinned.length > 0) {
            const lastLeftPinned = leftPinned[leftPinned.length - 1];
            lastLeftPinned.pinnedClass += ` ${styles.LastLeftPinned}`;
        }
        if (rightPinned.length > 0) {
            const firstRightPinned = rightPinned[0];
            firstRightPinned.pinnedClass += ` ${styles.FirstRightPinned}`;
        }

        return {
            leftPinnedColumns: leftPinned,
            rightPinnedColumns: rightPinned,
            scrollableColumns: scrollable,
            allColumnsOrdered: allOrdered,
        };
    }, [derivedColumns, pinnedColumnsMap, viewportWidth]);

    // Calculate minimum table width based on column widths
    const tableWidthInPixel = useMemo(() => {
        const totalWidthInPercentage = derivedColumns?.reduce?.((total, column) => {
            if (column.width && typeof column.width === 'string' && column.width.endsWith('%')) {
                const percentage = parseFloat(column.width);
                return total + percentage;
            }
            return total;
        }, 0);
        const widthPx = (totalWidthInPercentage / 100) * viewportWidth;
        return Math.max(widthPx, viewportWidth);
    }, [derivedColumns, viewportWidth, horizontal]);

    const renderOverlayScreen = (): React.ReactNode => {
        return (
            <ShimmerLoading
                headerLength={derivedColumns?.length ?? 0}
                headerWidths={derivedColumns?.map((col) => col.width || 'auto') ?? []}
                rowWidth={`${tableWidthInPixel}px`}
            />
        );
    };

    const getCellValue = useCallback(
        (row: any, column: DataTableColumn): React.ReactNode => {
            if (valueGetterMap?.[column.field]) {
                return valueGetterMap[column.field](row);
            }
            return lodashGet(row, column.field, '');
        },
        [valueGetterMap],
    );

    const getIsHighlightRow = useCallback(
        (assesseeOrgId: any, dealId: any) => {
            return (
                (commentThreadSource === COMPANIES_LIST && assesseeOrgId === commentThreadState.assesseeOrgId) ||
                (commentThreadSource === DEAL && dealId === commentThreadDealId) ||
                (commentThreadSource === INVESTOR_DEAL && dealId === commentThreadDealId)
            );
        },
        [commentThreadSource, commentThreadState.assesseeOrgId, commentThreadDealId],
    );

    /**
     * Determines if a row should be disabled based on deal status configuration
     *
     * This function checks multiple conditions to determine row disability:
     * 1. Global flag: isDisableRowLogicEnabled must be true in dealStatusConfig
     * 2. User type: Only applies to lender account types
     * 3. Status-specific flag: Individual status must have overrideDisableRow = true
     *
     * config structure (AICA_DEAL_NAVBAR_VIEW):
     * {
     *   dataConfig: {
     *     additionalDetailsConfig: {
     *       dealStatusDropdownOptions: {
     *         isDisableRowLogicEnabled: true,  // Master toggle for entire feature
     *         deal: [
     *           {
     *             value: "UNRESPONSIVE",
     *             overrideDisableRow: true  // This status will disable rows
     *           },
     *           {
     *             value: "INTEREST RECEIVED"
     *             // No overrideDisableRow - this status won't disable rows
     *           }
     *         ]
     *       }
     *     }
     *   }
     * }
     *
     * The config is stored in Redux state at: state.investor.deals.dealStatusConfig
     *
     * @param row - The row data object containing dealStatus property
     * @returns boolean - true if row should be disabled, false otherwise
     */
    const isRowDisabledRowCheck = (row: any) => {
        // Check if the global flag is enabled
        if (!dealStatusConfig?.isDisableRowLogicEnabled) {
            return false;
        }

        // Only apply to lender account types
        if (!isLenderType(userData.accountType)) {
            return false;
        }

        // Get the status option for the row's deal status from the pre-built map
        const statusOption = dealStatusConfig?.statusDropdownOptionsMap?.[row?.dealStatus];

        // If the status has overrideDisableRow flag set to true, disable the row
        return (statusOption as any)?.overrideDisableRow === true;
    };

    const renderHeaders = (): React.ReactNode => {
        if (horizontal) {
            // For horizontal display, no headers - it's a form-like layout
            return null;
        }

        return (
            <tr className={styles.DataTableHeaderRow}>
                {columnMetadata.allColumnsOrdered.map((metadata, index) => {
                    const { column, pinnedInfo, pinnedClass, headerStyle } = metadata;
                    if (column.field === 'borrowerRequirementsCount' && !dataPendingEnabledVisibility.show) return null;
                    return (
                        <th
                            key={`${column.field}-${index}`}
                            className={clsx(styles.DataTableHeaderCell, pinnedClass, {
                                [styles.ActionCell]: column.type === DataTableColumnType.ACTION,
                            })}
                            style={{
                                ...(column.headerStyle && { ...column.headerStyle }),
                                width: column.width ?? 'auto',
                                textAlign: column.headerAlign ?? 'left',
                                ...headerStyle,
                            }}
                        >
                            <div className={styles.HeaderContent}>
                                <span>{column.hideHeader ? '' : column.headerLabel}</span>
                            </div>
                        </th>
                    );
                })}
            </tr>
        );
    };

    // Memoized cell component to prevent unnecessary re-renders
    const MemoizedCell = React.memo<{
        metadata: ColumnMetadata;
        row: any;
        rowIndex: number;
        colIndex: number;
        cellValue: React.ReactNode;
        CellRenderer: React.ComponentType<any>;
        isRowDisabled?: boolean;
        dispatch: any;
    }>(({ metadata, row, rowIndex, colIndex, cellValue, CellRenderer, isRowDisabled, dispatch: cellDispatch }) => {
        const { column, pinnedClass, style } = metadata;

        // Check if this is the company name cell
        const isCompanyNameCell = column.field === COMPANY_NAME_FIELD;

        // Inline handlers to avoid dependency issues - following StatusDropdown pattern
        const handleMouseEnter = (event: React.MouseEvent<HTMLTableCellElement>) => {
            if (!isRowDisabled || !isCompanyNameCell) return;

            // Position tooltip just above the current cell's top border
            const cell = event.currentTarget as HTMLElement;
            const rect = cell.getBoundingClientRect();

            // Position virtual target higher within the cell to compensate for 10px offset
            // This makes tooltip appear right above current row, not in the row above
            cellDispatch(
                setTooltipContent({
                    content: <span style={{ color: '#000000' }}>{DISABLED_ROW_TOOLTIP_MESSAGE}</span>,
                    target: cell,
                    placement: 'top',
                    position: {
                        top: rect.top + window.scrollY + 15,
                        left: rect.left + window.scrollX,
                        width: rect.width,
                        height: 0,
                    },
                }),
            );
        };

        const handleMouseLeave = () => {
            if (!isRowDisabled || !isCompanyNameCell) return;
            removeTooltipContent(cellDispatch);
        };

        return (
            <td
                key={`${colIndex}-${rowIndex}`}
                className={clsx(styles.DataTableRowCell, pinnedClass, {
                    [styles.DisabledCompanyNameCell]: isRowDisabled && isCompanyNameCell,
                    [styles.ActionCell]: column.type === DataTableColumnType.ACTION,
                })}
                style={{
                    width: column.width || 'auto',
                    textAlign: column.align || 'left',
                    ...style,
                    ...((row as any).rowStyle || {}),
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div
                    className={clsx({
                        [styles.DisabledInteraction]: isRowDisabled && !column.bypassRowDisable,
                    })}
                >
                    {column?.isHistoryEnabled && onHistoryClick ? (
                        <div className={historyStyles.CellHistoryWrapper}>
                            <CellRenderer value={cellValue} row={row} column={column} rowIndex={rowIndex} columnIndex={colIndex} />
                            <HistoryTriggerButton column={column} row={row} onHistoryClick={onHistoryClick} />
                        </div>
                    ) : (
                        <CellRenderer value={cellValue} row={row} column={column} rowIndex={rowIndex} columnIndex={colIndex} />
                    )}
                </div>
            </td>
        );
    });

    /**
     * Horizontal Table Rendering
     *
     * When horizontal=true, the table displays data in a form-like layout where:
     * - Each column becomes a row with two cells: label and value
     * - Only the first data row is used for display
     * - Styling can be customized via props:
     *   - horizontalRowStyle: Style for each <tr> element
     *   - horizontalLabelStyle: Style for the label cell (first <td>)
     *   - horizontalValueStyle: Style for the value cell (second <td>)
     *   - horizontalLabelSuffix: Text appended to labels (default: ':')
     */
    const renderHorizontalRows = (): React.ReactNode => {
        return derivedColumns.map((column, rowIndex) => {
            const row = rows[0]; // Use first row for horizontal display
            if (!row) return null;

            const cellValue = getCellValue(row, column);
            const CellRenderer = cellRenderers[column.type];

            return (
                <tr
                    key={`${column.field}-${rowIndex}`}
                    className={clsx(styles.DataTableRow)}
                    style={{
                        border: 'none',
                        backgroundColor: 'transparent',
                        ...horizontalRowStyle,
                    }}
                >
                    <td
                        className={clsx(styles.DataTableRowCell)}
                        style={{
                            ...horizontalLabelStyle,
                        }}
                    >
                        {column.headerLabel}
                        {horizontalLabelSuffix}
                    </td>
                    <td
                        className={clsx(styles.DataTableRowCell)}
                        style={{
                            ...horizontalValueStyle,
                        }}
                    >
                        {CellRenderer ? (
                            <CellRenderer
                                key={`${rowIndex}`}
                                value={cellValue}
                                row={row}
                                column={column}
                                rowIndex={rowIndex}
                                columnIndex={0}
                            />
                        ) : (
                            <span style={{ color: '#666' }}>
                                {cellValue !== null &&
                                cellValue !== undefined &&
                                typeof cellValue === 'object' &&
                                !React.isValidElement(cellValue)
                                    ? '-'
                                    : cellValue || '-'}
                            </span>
                        )}
                    </td>
                </tr>
            );
        });
    };

    const renderRows = (): React.ReactNode => {
        if (horizontal) {
            return renderHorizontalRows();
        }

        return rows.map((row, rowIndex) => {
            const assesseeOrgId = (row as any)?.assesseeOrgId;
            const dealId = (row as any)?.dealId;
            const isHighlighted = getIsHighlightRow(assesseeOrgId, dealId);
            const rowDisabled = isRowDisabledRowCheck(row);

            return (
                <tr
                    key={`${(row as any)?.dealId}-${rowIndex}`}
                    className={clsx(styles.DataTableRow, {
                        [styles.RowDisabled]: rowDisabled,
                        [styles.RowWithOpenAction]: openActionRowIndex === rowIndex,
                        [styles.DataTableRow_Highlighted]: isHighlighted,
                    })}
                    style={(row as any).rowStyle}
                    onClick={(e) => {
                        if (!rowDisabled) {
                            onRowClick?.(e, row);
                        }
                    }}
                >
                    {columnMetadata.allColumnsOrdered.map((metadata, colIndex) => {
                        const { column } = metadata;
                        if (column.field === 'borrowerRequirementsCount' && !dataPendingEnabledVisibility.show) return null;
                        const cellValue = getCellValue(row, column);
                        const CellRenderer = cellRenderers[column.type];

                        if (!CellRenderer) {
                            // Handle cases where cellValue is an object (React can't render objects directly)
                            let displayValue = cellValue;
                            if (
                                cellValue !== null &&
                                cellValue !== undefined &&
                                typeof cellValue === 'object' &&
                                !React.isValidElement(cellValue)
                            ) {
                                // If it's an object and not a React element, don't try to render it
                                // This prevents "Objects are not valid as a React child" errors
                                displayValue = null;
                            }

                            return (
                                <td
                                    key={colIndex}
                                    className={clsx(styles.DataTableRowCell, metadata.pinnedClass)}
                                    style={{
                                        width: column.width || 'auto',
                                        textAlign: column.align || 'left',
                                        ...metadata.style,
                                    }}
                                >
                                    {displayValue}
                                </td>
                            );
                        }

                        return (
                            <MemoizedCell
                                key={`${colIndex}-${rowIndex}`}
                                metadata={metadata}
                                row={row}
                                rowIndex={rowIndex}
                                colIndex={colIndex}
                                cellValue={cellValue}
                                CellRenderer={CellRenderer}
                                isRowDisabled={rowDisabled}
                                dispatch={dispatch}
                            />
                        );
                    })}
                </tr>
            );
        });
    };

    const renderFilterDrawer = () =>
        customFilterDrawer.open && (
            <DataTableFilterDrawer
                open={customFilterDrawer.open}
                onClose={() => dispatch(setCustomFilterDrawer({ open: false }))}
                columns={derivedColumns}
                onApply={onFiltersApply || (() => {})}
                filterSourceKey={filterSourceKey ?? ''}
            />
        );

    const renderColumnCustomizationDrawer = () =>
        enableColumnCustomization &&
        masterConfig && (
            <DataTableColumnCustomizationDrawer
                masterConfig={masterConfig}
                pinnedColumns={pinnedColumns}
                onApply={handleApplyColumnCustomization}
            />
        );

    if (!loading && totalRowCount === 0 && isEmpty(rows)) {
        return (
            <>
                <div className={styles.EmptyState}>{emptyState}</div>
                {!horizontal && renderFilterDrawer()}
                {!horizontal && renderColumnCustomizationDrawer()}
            </>
        );
    }

    if (!loading && totalRowCount > 0 && rows.length === 0) {
        return (
            <>
                <div className={styles.EmptyState}>{noSearchResults ?? emptyState}</div>
                {!horizontal && renderFilterDrawer()}
                {!horizontal && renderColumnCustomizationDrawer()}
            </>
        );
    }

    return (
        <>
            <div className={styles.Container}>
                <div className={styles.DataTableContainer} ref={tableRef}>
                    <table className={styles.DataTable} style={{ width: `${tableWidthInPixel}px`, ...horizontalStyle }}>
                        {!horizontal && <thead className={styles.DataTableHead}>{renderHeaders()}</thead>}

                        {loading ? renderOverlayScreen() : <tbody className={styles.DataTableBody}>{renderRows()}</tbody>}
                    </table>
                </div>
            </div>
            {!horizontal && renderFilterDrawer()}
            {!horizontal && renderColumnCustomizationDrawer()}
        </>
    );
};

export default DataTable;
